
import { KeywordTagsInput } from '@/components/keyword-tags-input'
import { MultiSelect } from '@/components/multi-select'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useFilters } from '@/hooks/useFilters'
import {
  ActionConfig,
  ActionType,
  RoutingTargetType,
  TriggerConfig,
  TriggerType,
} from '@/interfaces/automation'
import { listAgentsQuery } from '@/queries/agent.query'
import { listAttendantsQuery } from '@/queries/attendant.query'
import { listAutomationsQuery } from '@/queries/automation.query'
import { listDepartmentsQuery } from '@/queries/department.query'
import { listFlowsQuery } from '@/queries/flow.query'
import { listTagsQuery } from '@/queries/tags.query'
import { AutomationService } from '@/services/automations.service'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { Automation } from '../schema'

interface AutomationMutateDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentRow?: Automation
}

const formSchema = z
  .object({
    name: z.string().min(1, 'Nome é obrigatório.'),
    description: z.string().optional(),
    triggerType: z.enum(['KEYWORD', 'SCHEDULE', 'EVENT', 'CONDITION']),
    triggerKeywords: z.array(z.string()).optional(),
    matchMode: z.enum(['contains', 'exact', 'starts_with', 'ends_with']).optional(),
    webhookUrl: z.string().optional(),
    webhookActionType: z.enum(['ROUTING', 'AGENT']).optional(),
    webhookFlowId: z.number().optional(),
    webhookAgentId: z.number().optional(),
    segmentIds: z.array(z.string()).optional(),
    actionType: z.enum(['ROUTING', 'AGENT']).optional(),
    actionTargetType: z.enum(['flow', 'department', 'attendant']).optional(),
    flowId: z.number().optional(),
    departmentId: z.number().optional(),
    attendantId: z.string().optional(),
    agentId: z.number().optional(),
    isActive: z.boolean(),
  })
  .refine((data) => data.triggerType === 'EVENT' || !!data.actionType, {
    message: 'Tipo de ação é obrigatório',
    path: ['actionType'],
  })
  .refine(
    (data) => data.triggerType !== 'KEYWORD' || (data.triggerKeywords && data.triggerKeywords.length > 0),
    {
      message: 'Adicione pelo menos uma palavra-chave',
      path: ['triggerKeywords'],
    }
  )
  .refine(
    (data) => data.triggerType !== 'CONDITION' || (data.segmentIds && data.segmentIds.length > 0),
    {
      message: 'Selecione pelo menos um segmento',
      path: ['segmentIds'],
    }
  )
  .refine((data) => data.triggerType !== 'EVENT' || !!data.webhookActionType, {
    message: 'Selecione o tipo de ação para o webhook',
    path: ['webhookActionType'],
  })
  .refine(
    (data) => data.triggerType !== 'EVENT' || data.webhookActionType !== 'AGENT' || !!data.webhookAgentId,
    {
      message: 'Selecione um agente',
      path: ['webhookAgentId'],
    }
  )
  .refine(
    (data) => data.triggerType !== 'EVENT' || data.webhookActionType !== 'ROUTING' || !!data.webhookFlowId,
    {
      message: 'Selecione um fluxo',
      path: ['webhookFlowId'],
    }
  )
  .refine(
    (data) => data.triggerType === 'EVENT' || data.actionType !== 'ROUTING' || !!data.actionTargetType,
    {
      message: 'Selecione o tipo de encaminhamento',
      path: ['actionTargetType'],
    }
  )
  .refine(
    (data) => data.triggerType === 'EVENT' || data.actionType !== 'ROUTING' || data.actionTargetType !== 'flow' || !!data.flowId,
    {
      message: 'Selecione um fluxo',
      path: ['flowId'],
    }
  )
  .refine(
    (data) =>
      data.triggerType === 'EVENT' ||
      data.actionType !== 'ROUTING' ||
      data.actionTargetType !== 'department' ||
      !!data.departmentId,
    {
      message: 'Selecione um departamento',
      path: ['departmentId'],
    }
  )
  .refine(
    (data) =>
      data.triggerType === 'EVENT' ||
      data.actionType !== 'ROUTING' ||
      data.actionTargetType !== 'attendant' ||
      !!data.attendantId,
    {
      message: 'Selecione um atendente',
      path: ['attendantId'],
    }
  )
  .refine(
    (data) => data.triggerType === 'EVENT' || data.actionType !== 'AGENT' || !!data.agentId,
    {
      message: 'Selecione um agente',
      path: ['agentId'],
    }
  )

type AutomationFormData = z.infer<typeof formSchema>

interface AutomationPayload {
  name: string
  description: string | null
  triggerType: TriggerType
  triggerConfig: TriggerConfig
  actionType: ActionType
  actionConfig: ActionConfig
  priority: number
  isActive: boolean
}

function getCurrentRoutingTargetType(currentRow?: Automation): RoutingTargetType | undefined {
  if (currentRow?.actionConfig.routing?.targetType) return currentRow.actionConfig.routing.targetType
  if (currentRow?.actionConfig.routing?.attendantId) return 'attendant'
  if (currentRow?.actionConfig.routing?.departmentId) return 'department'
  if (currentRow?.actionConfig.routing?.flowId) return 'flow'
  return undefined
}
function buildRoutingConfig(
  data: AutomationFormData,
  flows: Array<{ id: number; name: string }>,
  departments: Array<{ id: number; name: string }>,
  attendants: Array<{ userId: string; user: { name: string } }>,
) {
  if (data.actionTargetType === 'flow') {
    return {
      targetType: 'flow' as const,
      flowId: data.flowId,
      targetName: flows.find((flow) => flow.id === data.flowId)?.name,
    }
  }

  if (data.actionTargetType === 'department') {
    return {
      targetType: 'department' as const,
      departmentId: data.departmentId,
      targetName: departments.find((department) => department.id === data.departmentId)?.name,
    }
  }

  return {
    targetType: 'attendant' as const,
    attendantId: data.attendantId,
    targetName: attendants.find((attendant) => attendant.userId === data.attendantId)?.user.name,
  }
}

function buildAutomationPayload(
  data: AutomationFormData,
  attendants: Array<{ userId: string; user: { name: string } }>,
  departments: Array<{ id: number; name: string }>,
  flows: Array<{ id: number; name: string }>,
): AutomationPayload {
  let triggerConfig: TriggerConfig
  let actionType: ActionType
  let actionConfig: ActionConfig

  if (data.triggerType === 'KEYWORD') {
    triggerConfig = {
      keyword: {
        keywords: data.triggerKeywords || [],
        matchMode: data.matchMode || 'contains',
      },
    }
    actionType = data.actionType as ActionType
    actionConfig =
      data.actionType === 'ROUTING'
        ? { routing: buildRoutingConfig(data, flows, departments, attendants) }
        : { agent: { agentId: data.agentId! } }
  } else if (data.triggerType === 'EVENT') {
    triggerConfig = {
      webhook: {
        url: data.webhookUrl || '',
        actionType: data.webhookActionType!,
        agentId: data.webhookActionType === 'AGENT' ? data.webhookAgentId : undefined,
        flowId: data.webhookActionType === 'ROUTING' ? data.webhookFlowId : undefined,
      },
    }
    actionType = data.webhookActionType as ActionType
    actionConfig =
      data.webhookActionType === 'ROUTING'
        ? {
            routing: {
              targetType: 'flow',
              flowId: data.webhookFlowId!,
              targetName: flows.find((flow) => flow.id === data.webhookFlowId)?.name,
            },
          }
        : { agent: { agentId: data.webhookAgentId! } }
  } else {
    const segmentId = data.segmentIds && data.segmentIds.length > 0 ? Number(data.segmentIds[0]) : 0
    triggerConfig = {
      segment: {
        segmentId,
      },
    }
    actionType = data.actionType as ActionType
    actionConfig =
      data.actionType === 'ROUTING'
        ? { routing: buildRoutingConfig(data, flows, departments, attendants) }
        : { agent: { agentId: data.agentId! } }
  }

  return {
    name: data.name.trim(),
    description: data.description?.trim() || null,
    triggerType: data.triggerType,
    triggerConfig,
    actionType,
    actionConfig,
    priority: 10,
    isActive: data.isActive,
  }
}

export function AutomationMutateDrawer({
  open,
  onOpenChange,
  currentRow,
}: AutomationMutateDrawerProps) {
  const isUpdate = !!currentRow
  const queryClient = useQueryClient()
  const { filters } = useFilters('/_authenticated/automations/')

  const { data: flows = [] } = useQuery(listFlowsQuery)
  const { data: agents = [] } = useQuery(listAgentsQuery)
  const { data: segments = [] } = useQuery(listTagsQuery)
  const { data: departments = [] } = useQuery(listDepartmentsQuery)
  const { data: attendantsResponse } = useQuery(listAttendantsQuery)
  const attendants = attendantsResponse?.attendants ?? []
  const currentRoutingTargetType = getCurrentRoutingTargetType(currentRow)

  const form = useForm<AutomationFormData, any, AutomationFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: currentRow?.name ?? '',
      description: currentRow?.description ?? '',
      triggerType: currentRow?.triggerType ?? 'KEYWORD',
      triggerKeywords: currentRow?.triggerConfig.keyword?.keywords ?? [],
      matchMode: currentRow?.triggerConfig.keyword?.matchMode ?? 'contains',
      webhookUrl: currentRow?.triggerConfig.webhook?.url ?? '',
      webhookActionType: currentRow?.triggerConfig.webhook?.actionType ?? undefined,
      webhookFlowId: currentRow?.triggerConfig.webhook?.flowId ?? undefined,
      webhookAgentId: currentRow?.triggerConfig.webhook?.agentId ?? undefined,
      segmentIds: currentRow?.triggerConfig.segment?.segmentId
        ? [String(currentRow.triggerConfig.segment.segmentId)]
        : [],
      actionType:
        currentRow?.actionType === 'ROUTING' || currentRow?.actionType === 'AGENT'
          ? currentRow.actionType
          : undefined,
      actionTargetType: currentRoutingTargetType,
      flowId: currentRow?.actionConfig.routing?.flowId ?? undefined,
      departmentId: currentRow?.actionConfig.routing?.departmentId ?? undefined,
      attendantId: currentRow?.actionConfig.routing?.attendantId ?? undefined,
      agentId: currentRow?.actionConfig.agent?.agentId ?? undefined,
      isActive: currentRow?.isActive ?? true,
    },
  })

  const selectedTriggerType = form.watch('triggerType')
  const selectedActionType = form.watch('actionType')
  const selectedActionTargetType = form.watch('actionTargetType')
  const selectedWebhookActionType = form.watch('webhookActionType')
  const webhookUrl = form.watch('webhookUrl')

  const [isGeneratingWebhook, setIsGeneratingWebhook] = useState(false)
  const generateWebhookUrl = async () => {
    if (!currentRow?.id) {
      const tempId = Math.random().toString(36).substring(2, 15)
      const newUrl = `https://api.wazai.com/webhook/${tempId}`
      form.setValue('webhookUrl', newUrl)
      toast.success('URL do webhook gerada')
      return
    }

    setIsGeneratingWebhook(true)
    try {
      const response = await AutomationService.generateWebhookUrl(currentRow.id)
      form.setValue('webhookUrl', response.url)
      toast.success('URL do webhook gerada')
    } catch {
      toast.error('Erro ao gerar URL do webhook')
    } finally {
      setIsGeneratingWebhook(false)
    }
  }

  const copyWebhookUrl = () => {
    if (webhookUrl) {
      navigator.clipboard.writeText(webhookUrl)
      toast.success('URL copiada para a área de transferência')
    }
  }

  const onSubmit = form.handleSubmit((data) => {
    const payload = buildAutomationPayload(data, attendants, departments, flows)

    const mutation =
      isUpdate && currentRow
        ? AutomationService.update(currentRow.id, payload)
        : AutomationService.create(payload)

    toast.promise(mutation, {
      loading: 'Salvando automação...',
      success: () => {
        onOpenChange(false)
        form.reset()
        queryClient.invalidateQueries({
          queryKey: listAutomationsQuery(filters).queryKey,
        })
        return isUpdate ? 'Automação atualizada' : 'Automação criada'
      },
      error: () =>
        isUpdate
          ? 'Ocorreu um erro ao tentar atualizar a automação.'
          : 'Ocorreu um erro ao tentar criar a automação.',
    })
  })

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v)
        form.reset()
      }}
    >
      <SheetContent className='flex flex-col overflow-y-auto'>
        <SheetHeader className='text-left'>
          <SheetTitle>{isUpdate ? 'Atualizar' : 'Adicionar'} Automação</SheetTitle>
          <SheetDescription>
            {isUpdate
              ? 'Atualize a automação provendo as informações necessárias.'
              : 'Adicione uma nova automação provendo as informações necessárias.'}
            Clique em salvar quando estiver pronto.
          </SheetDescription>
        </SheetHeader>
        <Form {...form}>
          <form id='automations-form' onSubmit={onSubmit} className='flex-1 space-y-5'>
            <FormField
              control={form.control}
              name='name'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input {...field} autoFocus placeholder='Digite o nome da automação' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='description'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Descrição</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder='Digite uma descrição para a automação' rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='triggerType'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Tipo de Trigger</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder='Selecione o tipo de trigger' />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value='KEYWORD'>Palavra-chave</SelectItem>
                      <SelectItem value='CONDITION'>Segmento</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {selectedTriggerType === 'KEYWORD' && (
              <>
                <FormField
                  control={form.control}
                  name='triggerKeywords'
                  render={({ field }) => (
                    <FormItem className='space-y-1'>
                      <FormLabel>Palavras-chave</FormLabel>
                      <FormControl>
                        <KeywordTagsInput
                          value={field.value || []}
                          onChange={field.onChange}
                          placeholder='Digite uma palavra-chave e pressione Enter'
                        />
                      </FormControl>
                      <FormDescription>
                        Pressione Enter ou ponto e vírgula para adicionar cada palavra-chave
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='matchMode'
                  render={({ field }) => (
                    <FormItem className='space-y-1'>
                      <FormLabel>Modo de Correspondência</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder='Selecione o modo' />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value='contains'>Contém</SelectItem>
                          <SelectItem value='exact'>Exato</SelectItem>
                          <SelectItem value='starts_with'>Começa com</SelectItem>
                          <SelectItem value='ends_with'>Termina com</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>Como as palavras-chave serão comparadas</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}
            {selectedTriggerType === 'EVENT' && (
              <>
                <div className='space-y-3 rounded-lg border bg-muted/50 p-4'>
                  <FormLabel>URL do Webhook</FormLabel>
                  {webhookUrl ? (
                    <div className='flex gap-2'>
                      <Input value={webhookUrl} readOnly className='font-mono text-xs' />
                      <Button type='button' size='sm' variant='outline' onClick={copyWebhookUrl}>
                        <Copy className='h-4 w-4' />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type='button'
                      size='sm'
                      variant='outline'
                      onClick={generateWebhookUrl}
                      disabled={isGeneratingWebhook}
                    >
                      {isGeneratingWebhook ? (
                        <>
                          <RefreshCw className='mr-2 h-4 w-4 animate-spin' />
                          Gerando...
                        </>
                      ) : (
                        <>
                          <RefreshCw className='mr-2 h-4 w-4' />
                          Gerar URL do Webhook
                        </>
                      )}
                    </Button>
                  )}
                  <FormDescription>
                    Use esta URL para disparar a automação via requisição POST
                  </FormDescription>
                </div>
                <FormField
                  control={form.control}
                  name='webhookActionType'
                  render={({ field }) => (
                    <FormItem className='space-y-1'>
                      <FormLabel>Tipo de Ação do Webhook</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder='Selecione o tipo de ação' />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value='ROUTING' disabled={flows.length === 0}>
                            Executar Fluxo WhatsApp
                          </SelectItem>
                          <SelectItem value='AGENT' disabled={agents.length === 0}>
                            Executar Agente Conversacional
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {selectedWebhookActionType === 'ROUTING' && (
                  <FormField
                    control={form.control}
                    name='webhookFlowId'
                    render={({ field }) => (
                      <FormItem className='space-y-1'>
                        <FormLabel>Selecionar Fluxo</FormLabel>
                        <Select
                          onValueChange={(value) => field.onChange(Number(value))}
                          value={field.value?.toString()}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder='Selecione um fluxo' />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {flows.map((flow) => (
                              <SelectItem key={flow.id} value={flow.id.toString()}>
                                {flow.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                {selectedWebhookActionType === 'AGENT' && (
                  <FormField
                    control={form.control}
                    name='webhookAgentId'
                    render={({ field }) => (
                      <FormItem className='space-y-1'>
                        <FormLabel>Selecionar Agente</FormLabel>
                        <Select
                          onValueChange={(value) => field.onChange(Number(value))}
                          value={field.value?.toString()}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder='Selecione um agente' />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {agents.map((agent) => (
                              <SelectItem key={agent.id} value={agent.id.toString()}>
                                {agent.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </>
            )}
            {selectedTriggerType === 'CONDITION' && (
              <FormField
                control={form.control}
                name='segmentIds'
                render={({ field }) => (
                  <FormItem className='space-y-1'>
                    <FormLabel>Selecionar Segmento</FormLabel>
                    <MultiSelect
                      placeholder='Selecione um segmento'
                      options={segments.map((segment) => ({
                        label: segment.name,
                        value: segment.id.toString(),
                      }))}
                      onValueChange={field.onChange}
                      defaultValue={field.value || []}
                      disabled={segments.length === 0}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            {selectedTriggerType !== 'EVENT' && (
              <>
                <FormField
                  control={form.control}
                  name='actionType'
                  render={({ field }) => (
                    <FormItem className='space-y-1'>
                      <FormLabel>Tipo de Ação</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value)
                          if (value !== 'ROUTING') {
                            form.setValue('actionTargetType', undefined)
                            form.setValue('flowId', undefined)
                            form.setValue('departmentId', undefined)
                            form.setValue('attendantId', undefined)
                          }
                        }}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder='Selecione o tipo de a��o' />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value='ROUTING'>Encaminhar Atendimento</SelectItem>
                          <SelectItem value='AGENT' disabled={agents.length === 0}>
                            Executar Agente IA
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {selectedActionType === 'ROUTING' && (
                  <>
                    <FormField
                      control={form.control}
                      name='actionTargetType'
                      render={({ field }) => (
                        <FormItem className='space-y-1'>
                          <FormLabel>Encaminhar para</FormLabel>
                          <Select
                            onValueChange={(value: RoutingTargetType) => {
                              field.onChange(value)
                              form.setValue('flowId', undefined)
                              form.setValue('departmentId', undefined)
                              form.setValue('attendantId', undefined)
                            }}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder='Selecione o destino' />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value='flow'>Fluxo</SelectItem>
                              <SelectItem value='department'>Departamento</SelectItem>
                              <SelectItem value='attendant'>Atendente</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {selectedActionTargetType === 'flow' && (
                      <FormField
                        control={form.control}
                        name='flowId'
                        render={({ field }) => (
                          <FormItem className='space-y-1'>
                            <FormLabel>Selecionar Fluxo</FormLabel>
                            <Select
                              onValueChange={(value) => field.onChange(Number(value))}
                              value={field.value?.toString()}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder='Selecione um fluxo' />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {flows.map((flow) => (
                                  <SelectItem key={flow.id} value={flow.id.toString()}>
                                    {flow.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                    {selectedActionTargetType === 'department' && (
                      <FormField
                        control={form.control}
                        name='departmentId'
                        render={({ field }) => (
                          <FormItem className='space-y-1'>
                            <FormLabel>Selecionar Departamento</FormLabel>
                            <Select
                              onValueChange={(value) => field.onChange(Number(value))}
                              value={field.value?.toString()}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder='Selecione um departamento' />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {departments.map((department) => (
                                  <SelectItem key={department.id} value={department.id.toString()}>
                                    {department.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                    {selectedActionTargetType === 'attendant' && (
                      <FormField
                        control={form.control}
                        name='attendantId'
                        render={({ field }) => (
                          <FormItem className='space-y-1'>
                            <FormLabel>Selecionar Atendente</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder='Selecione um atendente' />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {attendants.map((attendant) => (
                                  <SelectItem key={attendant.userId} value={attendant.userId}>
                                    {attendant.user.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </>
                )}
                {selectedActionType === 'AGENT' && (
                  <FormField
                    control={form.control}
                    name='agentId'
                    render={({ field }) => (
                      <FormItem className='space-y-1'>
                        <FormLabel>Selecionar Agente</FormLabel>
                        <Select
                          onValueChange={(value) => field.onChange(Number(value))}
                          value={field.value?.toString()}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder='Selecione um agente' />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {agents.map((agent) => (
                              <SelectItem key={agent.id} value={agent.id.toString()}>
                                {agent.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </>
            )}
            <FormField
              control={form.control}
              name='isActive'
              render={({ field }) => (
                <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                  <div className='space-y-0.5'>
                    <FormLabel className='text-base'>Automação Ativa</FormLabel>
                    <FormDescription>A automação será executada quando ativa</FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
          </form>
        </Form>
        <SheetFooter className='gap-2'>
          <SheetClose asChild>
            <Button variant='outline'>Cancelar</Button>
          </SheetClose>
          <Button form='automations-form' type='submit'>
            {isUpdate ? 'Atualizar' : 'Adicionar'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
